import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import { Warning as WarningIcon, ContentCopy as RevisionIcon } from '@mui/icons-material';

interface RevisionConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  loading?: boolean;
}

const RevisionConfirmDialog: React.FC<RevisionConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  loading = false
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <WarningIcon color="warning" />
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Alert 
          severity="info" 
          icon={<RevisionIcon />}
          sx={{ mb: 2 }}
        >
          <AlertTitle>Revision Required</AlertTitle>
          {message}
        </Alert>
        
        <Box sx={{ 
          bgcolor: 'grey.50', 
          p: 2, 
          borderRadius: 1,
          border: 1,
          borderColor: 'grey.200'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>What happens when you create a revision:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • The original version will be preserved for users who have already started it<br/>
            • A new editable version will be created with all content copied<br/>
            • You can make changes to the new version without affecting existing users<br/>
            • New users will see the updated version
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
        <Button 
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={<RevisionIcon />}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Creating...' : 'Create Revision'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionConfirmDialog;


